/** AssistantPane (chats SPA tab variant): message list with markdown, real-time ChatRoom DO updates, and the message input. */

import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { LAYOUT_CLASSES } from '@federa/components/common/layout-styles';
import { FULL_HEIGHT_STYLE } from '@federa/const/style-size-presets';
import { DOM_EVENTS } from '@federa/const/websocket-actions-dom';
import { FLEX_COLUMN_STYLE, FLEX_FILL_TRUNCATE_STYLE } from '@federa/const/style-flex-presets';
import { MessageList } from './AssistantPane.components.message.tsx';
import { LoadOlderMessages, MessagesSkeleton, EmptyMessagesState } from './AssistantPane.components.conversation.tsx';
import { AgentStatusRow } from './AssistantPane.components.agent-status.tsx';
import { useAssistantPane } from './AssistantPane.hooks.ts';
import { HeaderSection } from './AssistantPane.header.tsx';
import { InputArea } from './AssistantPane.input.tsx';
import type { CSSProperties } from 'react';
import type { ChatMessageStruct } from '@federa/types/conversations-chat-structs';
import type { MessageHistoryPaging } from '@federa/types/ui-chat-messaging';

function MessagesContent({
	initializing,
	displayMessages,
	username,
	userId,
	messagesEndRef,
	historyPaging,
}: {
	initializing: boolean;
	displayMessages: ChatMessageStruct[];
	username: string;
	userId: string;
	messagesEndRef: React.RefObject<HTMLDivElement | null>;
	historyPaging: MessageHistoryPaging;
}) {
	if (initializing && displayMessages.length === 0) {
		return <MessagesSkeleton />;
	}
	if (displayMessages.length === 0) {
		return <EmptyMessagesState />;
	}
	return (
		<>
			{/* ABOVE the list: the conversation opens on its newest group and walks backward, so the scrollback control
			    belongs at the end the reader is scrolling toward. It renders nothing once the history has no more groups. */}
			<LoadOlderMessages
				hasOlder={historyPaging.hasOlderMessages}
				loading={historyPaging.loadingOlderMessages}
				onLoadOlder={historyPaging.loadOlderMessages}
			/>
			<MessageList messages={displayMessages} username={username} userId={userId} />
			<div ref={messagesEndRef} />
		</>
	);
}

/** Container style: fill the tab pane and own the internal scroll. `maxHeight: 100dvh` + `overflow: hidden` are a HARD
 *  cap: a slightly under-constrained ancestor makes the flex-1 list grow to full content instead of scrolling. */
const ASSISTANT_PANE_STYLE: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	...FLEX_FILL_TRUNCATE_STYLE,
	minHeight: 0,
	maxHeight: '100dvh',
	overflow: 'hidden',
	...FULL_HEIGHT_STYLE,
	backgroundColor: colors.background.primary,
	borderColor: colors.border.muted,
};

export function AssistantPane({ embedded = false }: { embedded?: boolean }) {
	const {
		paneRef,
		canSwitchConversations,
		showConversationList,
		setShowConversationList,
		sortedConversations,
		currentConversation,
		handleSelectConversation,
		initializing,
		displayMessages,
		user,
		messagesEndRef,
		messaging,
		sendError,
		setSendError,
		selectedFiles,
		handleFilesSelected,
		handleRemoveFile,
		isUploading,
		uploadProgress,
		inputRef,
		input,
		handleInputChange,
		handleKeyDown,
		wsConnected,
		everConnected,
		handleSendMessage,
		canSend,
		loadOlderMessages,
		loadingOlderMessages,
		hasOlderMessages,
	} = useAssistantPane();

	const conversationTitle = currentConversation?.title || 'Assistant';
	const hasConversation = Boolean(currentConversation);

	// Embedded in the host desktop side panel: the header X dispatches the panel-close event the federa shell listens for (cross-MFE-boundary). The standalone /assistant route and the admin tab are always visible, so no X.
	const handleClose = embedded ? () => window.dispatchEvent(new Event(DOM_EVENTS.ASSISTANT_PANEL_CLOSE)) : undefined;

	return (
		<section ref={paneRef} aria-label="Alex assistant" style={ASSISTANT_PANE_STYLE}>
			<HeaderSection
				canSwitchConversations={canSwitchConversations}
				conversationTitle={conversationTitle}
				showConversationList={showConversationList}
				setShowConversationList={setShowConversationList}
				conversations={sortedConversations}
				currentConversationId={currentConversation?.id}
				onSelectConversation={handleSelectConversation}
				onClose={handleClose}
			/>

			<div className={`${LAYOUT_CLASSES.scrollPane} min-h-0`} style={{ padding: spacing[4] }}>
				<MessagesContent
					initializing={initializing}
					displayMessages={displayMessages}
					username={user.username}
					userId={user.userId}
					messagesEndRef={messagesEndRef}
					historyPaging={{ loadOlderMessages, loadingOlderMessages, hasOlderMessages }}
				/>
			</div>

			{messaging.agentStatus && <AgentStatusRow frame={messaging.agentStatus} />}

			<InputArea
				sendError={sendError}
				setSendError={setSendError}
				selectedFiles={selectedFiles}
				handleFilesSelected={handleFilesSelected}
				handleRemoveFile={handleRemoveFile}
				isUploading={isUploading}
				uploadProgress={uploadProgress}
				hasConversation={hasConversation}
				inputRef={inputRef}
				input={input}
				handleInputChange={handleInputChange}
				handleKeyDown={handleKeyDown}
				wsConnected={wsConnected}
				everConnected={everConnected}
				handleSendMessage={handleSendMessage}
				canSend={canSend}
			/>
		</section>
	);
}
